import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {FITHGameConfig} from "../../FITHGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {TimelineLite, TweenLite} from "gsap";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {FITHGameAssets} from "../../FITHGameAssets";
import {FITHGameModuleConfig} from "../../FITHGameModuleConfig";
import {ShakingDisplayObject} from "@nolimitcity/slot-game/bin/core/displayobject/ShakingDisplayObject";
import {FITHSoundManager} from "../../sound/FITHSoundManager";

export class FITHWildMiningView extends BaseView {
    private _wildMiningBanner: PIXI.spine.Spine;

    private _bannerTl: TimelineLite;
    private _nonTargetTl: TimelineLite;
    private _targetTl: TimelineLite;

    private _shakeObjs: ShakingDisplayObject[] = [];

    private _isAborted: boolean = false;

    private _shakeSoundPlayed: boolean = false;
    private _symbolsTurnWildSoundPlayed: boolean = false;
    private _symbolsExplodeBigSoundPlayed: boolean = false;

    private static clearSymbol(sym: StateReelSymbol): void {
        sym.setVisibility(false);
        sym.removeChildren();
    }

    private static finishTl(tl: TimelineLite): void {
        if (tl && tl.isActive()) {
            tl.progress(1);
        }
    }

    public playWildMiningBanner(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        this._isAborted = false;
        this._shakeSoundPlayed = false;
        this._symbolsTurnWildSoundPlayed = false;
        this._symbolsExplodeBigSoundPlayed = false;

        // as per #77
        tl.add([
            () => {
                // #218
                FITHSoundManager.playTextSweep();

                this._wildMiningBanner.updateTransform();
                this._wildMiningBanner.visible = true;
            },
            new SpineTween(this._wildMiningBanner, 0, "base")
        ], 0);

        tl.add(() => this._wildMiningBanner.visible = false, 27 / 30);

        this._bannerTl = tl;
        return tl;
    }

    public playNonTargetSymbolAnimations(reelId: number, symId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            sym: StateReelSymbol = Reels.getSymbol(reelId, symId)!,
            darkImg: PIXI.Graphics = new PIXI.Graphics,
            darkImgWidth: number = FITHGameConfig.instance.SYMBOL_WIDTH,
            darkImgHeight: number = FITHGameConfig.instance.SYMBOL_HEIGHT;

        darkImg.beginFill(0x000000, 1);
        darkImg.drawRect(-(darkImgWidth / 2), -(darkImgHeight / 2), darkImgWidth, darkImgHeight);
        darkImg.endFill();
        darkImg.alpha = 0;
        sym.addChild(darkImg);

        // as per #77
        tl.to(darkImg, 5 / 30, {alpha: 0.4}, 0);

        tl.to(sym.scale, 5 / 30, {x: 0.75, y: 0.75}, 25 / 30);

        tl.add([
            () => {
                FITHWildMiningView.clearSymbol(sym);

                // #218
                if (!this._symbolsExplodeBigSoundPlayed) {
                    this._symbolsExplodeBigSoundPlayed = true;
                    FITHSoundManager.playSymbolsExplodeBig();
                }
            },
            FITHGameModuleConfig.symbolWinAnimations.playWildMiningAnimation(reelId, symId)
        ], 30 / 30);

        this._nonTargetTl = tl;
        return tl;
    }

    public playTargetSymbolAnimations(reelId: number, symId: number, allWildPosReelId: number[], allWildPosSymId: number[], targetPositions: number[][]): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            sym: StateReelSymbol = Reels.getSymbol(reelId, symId)!,
            shakeSym: ShakingDisplayObject = new ShakingDisplayObject(sym),
            symWidth: number = FITHGameConfig.instance.SYMBOL_WIDTH,
            symHeight: number = FITHGameConfig.instance.SYMBOL_HEIGHT,
            baseX: number = 60,
            baseY: number = FITHGameConfig.instance.REEL_AREA_POS_Y + (symHeight / 2);

        let toX: number,
            screenSym: PIXI.Sprite,
            wildCombine: PIXI.spine.Spine;

        // as per #77
        tl.add(() => {
            screenSym = new PIXI.Sprite(FITHGameAssets.getSymbolStateTextures(sym.symName)[0]);
            screenSym.blendMode = PIXI.BLEND_MODES.SCREEN;
            screenSym.anchor.set(0.5);
            sym.addChild(screenSym);
        }, 0);

        tl.to(sym.scale, 3 / 30, {x: 1.3, y: 1.3}, 0);
        tl.to(sym.scale, 2 / 30, {x: 1.1, y: 1.1}, 3 / 30);

        this._shakeObjs.push(shakeSym);
        tl.add(() => {
            if (!this._isAborted) {
                // #218
                if (!this._shakeSoundPlayed) {
                    this._shakeSoundPlayed = true;
                    FITHSoundManager.playSymbolsShake();
                }
                shakeSym.shake(0.1, 2);
            }
        }, 10 / 30);
        tl.add(() => {
            if (!this._isAborted) {
                shakeSym.stopShake();
            }
        }, 22 / 30);

        // #218
        if (!this._symbolsTurnWildSoundPlayed) {
            this._symbolsTurnWildSoundPlayed = true;
            tl.add(() => FITHSoundManager.playSymbolsTurnWild(), 22 / 30);
        }

        if ((allWildPosReelId[0] - 1) === reelId) {
            toX = sym.x + symWidth;
            tl.to(sym, 3 / 30, {x: toX}, 22 / 30);
        } else if ((allWildPosReelId[allWildPosReelId.length - 1] + 1) === reelId) {
            toX = sym.x - symWidth;
            tl.to(sym, 3 / 30, {x: toX}, 22 / 30);
        }

        // #234
        if (allWildPosSymId.includes(symId)) {
            tl.add(() => FITHWildMiningView.clearSymbol(sym), 25 / 30);
        }

        // #234
        if (targetPositions[reelId] && targetPositions[reelId].includes(symId)) {
            wildCombine = new PIXI.spine.Spine(GameResources.getSpineAsset("W_spine"));
            wildCombine.visible = false;
            wildCombine.position.set(baseX + (reelId * symWidth), baseY + (symId * symHeight));
            this.addChild(wildCombine);

            tl.add([
                () => {
                    wildCombine.updateTransform();
                    wildCombine.visible = true;

                    // #146
                    TweenLite.to(StageManager.stage, 0.2, {x: "+=" + 20, y: "+=" + 20, ease: "shakeBounce"});
                },
                new SpineTween(wildCombine, 0, "combin")
            ], 23 / 30);
            tl.add(() => {
                sym.replaceWith("W", reelId);

                wildCombine.visible = false;
                this.removeChild(wildCombine);
            }, 35 / 30);
        }

        this._targetTl = tl;
        return tl;
    }

    public abort(): void {
        this._isAborted = true;

        FITHWildMiningView.finishTl(this._bannerTl);
        FITHWildMiningView.finishTl(this._nonTargetTl);
        FITHWildMiningView.finishTl(this._targetTl);

        this._shakeObjs.forEach((obj: ShakingDisplayObject) => obj.stopShake());
        this._shakeObjs = [];
    }

    protected initAnimations(): void {
        super.initAnimations && super.initAnimations();

        StageManager.getLayer(FITHGameConfig.instance.LAYERS.WILD_MINING.name).addChild(this);

        this.createWildMiningBanner();
    }

    private createWildMiningBanner(): void {
        this._wildMiningBanner = new PIXI.spine.Spine(GameResources.getSpineAsset("WildMining"));
        this._wildMiningBanner.visible = false;
        this._wildMiningBanner.position.set(FITHGameConfig.instance.REEL_AREA_WIDTH * 0.5);
        this.addChild(this._wildMiningBanner);
    }
}